import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Form, Field } from 'react-final-form'
import {
  Button,
  FormControl,
  FormControlLabel,
  MenuItem,
} from '@material-ui/core'
import { injectIntl } from 'react-intl'
import getValidator, {
  VALIDATOR_EMAIL,
  VALIDATOR_IS_EMPTY,
  VALIDATOR_IS_LESS_THEN_255_SYMBOLS,
} from 'validation'
import { Checkbox, Select } from 'components/common/FormInputs'
import { APP_LANGUAGES } from 'constants/index'
import RoleHelper from 'helpers/role'
import { REACT_INTL_PROP_TYPE } from 'constants/propTypes'
import CircleLoader from '../Loading/CircleLoader'
import Input from '../common/Form/Input'
import messages from './translate'
import SelectInput from '../common/Form/SelectInput'

function UserForm({
  applications,
  siteList,
  userGroupList,
  onSubmit,
  handleLicenseeChange,
  handleSiteChange,
  intl: { formatMessage },
  user,
  roles,
  licensees,
  roleLevel,
}) {
  const [jobsControlState, setJobsControlState] = useState(false)
  const [licensee, setLicensee] = useState(null)
  const [site, setSite] = useState(null)
  const langList = Object.values(APP_LANGUAGES)
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    locale: '',
    enabled: true,
    username: '',
    licensee: 0,
    site: null,
    canCreateJobs: false,
    canDeleteJobs: false,
    canDeleteJobSeries: false,
    canManageWorkflows: false,
    canReopenJobs: false,
    canSeeDeletedJobs: false,
    canRecoverDeletedJobs: false,
    canSetSensorsLimits: false,
    roles: [],
    userGroups: [],
  })
  const nonEmptyValidator = []

  useEffect(() => {
    if (user && user.licensee && Object.keys(user).length) {
      const {
        name,
        email,
        locale,
        enabled,
        username,
        canDeleteJobs,
        canDeleteJobSeries,
        canCreateJobs,
        canManageWorkflows,
        canReopenJobs,
        canSeeDeletedJobs,
        canRecoverDeletedJobs,
        canSetSensorsLimits,
        roles = [],
        licensee: { id: licenseeId },
        site: { id: siteId },
        userGroups = [],
      } = user

      if (email) {
        nonEmptyValidator.push(VALIDATOR_IS_EMPTY)
      }

      setFormState({
        name,
        email,
        locale,
        enabled,
        username,
        licensee: licenseeId,
        site: siteId,
        canDeleteJobs,
        canDeleteJobSeries,
        canCreateJobs,
        canManageWorkflows,
        canReopenJobs,
        canSeeDeletedJobs,
        canRecoverDeletedJobs,
        canSetSensorsLimits,
        roles,
        userGroups: userGroups.map(group => group.id),
      })
    }
  }, [user])

  useEffect(() => {
    const controlState = !!applications.find(
      ({ appTitle }) => appTitle === 'APP_MY_JOBS',
    )

    setJobsControlState(controlState)
  }, [applications])

  useEffect(() => {
    if (licensee) {
      handleLicenseeChange(licensee)
    }
  }, [licensee])

  useEffect(() => {
    if (site) {
      handleSiteChange(site, licensee)
    }
  }, [site])

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={formState}
      mutators={{
        handleCanSeeDeletedJobsChange: ([value], state, { changeValue }) => {
          if (!value) {
            changeValue(state, 'canRecoverDeletedJobs', () => false)
          }
        },
        handleFieldChange: ([value, field], state, { changeValue }) => {
          const formState = { ...state }
          const {
            formState: {
              submitErrors: { [field]: fieldError } = {},
              values: { [field]: fieldValue },
            },
          } = formState

          if (fieldError && fieldValue && value !== fieldValue) {
            delete formState.formState.submitErrors[field]
          }

          changeValue(formState, field, () => value)
        },
        onLicenseeChange: (
          [
            {
              target: { value },
            },
          ],
          state,
          { changeValue },
        ) => {
          setLicensee(value)
          changeValue(state, 'licensee', () => value)
          changeValue(state, 'site', () => 0)
          changeValue(state, 'userGroups', () => [])
        },
        onSiteChange: (
          [
            {
              target: { value },
            },
          ],
          state,
          { changeValue },
        ) => {
          setSite(value)
          changeValue(state, 'site', () => value)
        },
      }}
      render={({
        handleSubmit,
        submitting,
        values,
        pristine,
        form: { mutators },
      }) => (
        <form onSubmit={handleSubmit} className="form-basic">
          <CircleLoader loading={submitting} />
          <Field
            required
            name="name"
            type="text"
            label={formatMessage(messages.name)}
            component={Input}
            className="form-control"
            disabled={submitting}
            validate={getValidator([
              VALIDATOR_IS_EMPTY,
              VALIDATOR_IS_LESS_THEN_255_SYMBOLS,
            ])}
          />
          <Field
            name="username"
            type="text"
            label={formatMessage(messages.username)}
            component={Input}
            className="form-control"
            disabled={submitting}
            validate={getValidator(VALIDATOR_IS_LESS_THEN_255_SYMBOLS)}
            InputProps={{
              onChange: ({ target: { value } }) => {
                mutators.handleFieldChange(value, 'username')
              },
            }}
          />
          <Field
            name="email"
            type="email"
            label={formatMessage(messages.email)}
            component={Input}
            className="form-control"
            disabled={submitting}
            required={!!user && !!user.email}
            validate={getValidator([
              VALIDATOR_EMAIL,
              VALIDATOR_IS_LESS_THEN_255_SYMBOLS,
              ...nonEmptyValidator,
            ])}
            InputProps={{
              onChange: ({ target: { value } }) => {
                mutators.handleFieldChange(value, 'email')
              },
            }}
          />
          {user && roleLevel >= RoleHelper.ROLE_SUPERADMIN_LEVEL && (
            <Field
              name="licensee"
              component={SelectInput}
              label={formatMessage(messages.licensee)}
              disabled={submitting}
              onChange={(args, state, utils) => {
                mutators.onLicenseeChange(args, state, utils)
              }}>
              {licensees.map(({ id, name }) => (
                <MenuItem button key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
            </Field>
          )}
          <Field
            required
            name="site"
            component={SelectInput}
            label={formatMessage(messages.site)}
            disabled={submitting}
            validate={getValidator([VALIDATOR_IS_EMPTY])}
            onChange={(args, state, utils) => {
              mutators.onSiteChange(args, state, utils)
            }}>
            {siteList.map(({ id: siteId, name: siteName }) => (
              <MenuItem button key={siteId} value={siteId}>
                {siteName}
              </MenuItem>
            ))}
          </Field>
          <FormControl className="form-group" fullWidth>
            <Field
              multiple
              name="userGroups"
              component={Select}
              format={value => value || []}
              label={formatMessage(messages.groups)}
              disabled={submitting}
              formControlProps={{
                fullWidth: true,
              }}
              inputProps={{
                multiple: true,
              }}>
              {userGroupList.map(({ id, name }) => (
                <MenuItem button key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
            </Field>
          </FormControl>
          <FormControl className="form-group" fullWidth>
            <Field
              multiple
              name="roles"
              component={Select}
              label={formatMessage(messages.roles)}
              format={value => value || []}
              disabled={submitting}
              formControlProps={{
                fullWidth: true,
                required: true,
              }}
              inputProps={{
                multiple: true,
              }}
              validate={getValidator([VALIDATOR_IS_EMPTY])}>
              {Object.keys(roles).map(key => (
                <MenuItem button key={key} value={key}>
                  {roles[key]}
                </MenuItem>
              ))}
            </Field>
          </FormControl>
          <div className="form-group">
            <Field
              name="locale"
              component={Select}
              label={formatMessage(messages.locale)}
              disabled={submitting}
              formControlProps={{
                fullWidth: true,
                required: true,
              }}
              validate={getValidator([VALIDATOR_IS_EMPTY])}>
              {langList.map(({ key, title }) => (
                <MenuItem button key={key} value={key}>
                  {formatMessage({ id: title })}
                </MenuItem>
              ))}
            </Field>
          </div>
          <div className="form-group">
            <FormControl>
              <FormControlLabel
                label={formatMessage(messages.active)}
                disabled={submitting}
                className="form-label"
                control={
                  <Field
                    name="enabled"
                    component={Checkbox}
                    type="checkbox"
                    color="primary"
                  />
                }
              />
            </FormControl>
          </div>
          {jobsControlState && (
            <>
              <div className="form-group">
                <FormControl>
                  <FormControlLabel
                    label={formatMessage(messages.canCreateJobs)}
                    disabled={submitting}
                    className="form-label"
                    control={
                      <Field
                        name="canCreateJobs"
                        component={Checkbox}
                        type="checkbox"
                        color="primary"
                      />
                    }
                  />
                </FormControl>
              </div>
              <div className="form-group">
                <FormControl>
                  <FormControlLabel
                    label={formatMessage(messages.canDeleteJobs)}
                    disabled={submitting}
                    className="form-label"
                    control={
                      <Field
                        name="canDeleteJobs"
                        component={Checkbox}
                        type="checkbox"
                        color="primary"
                      />
                    }
                  />
                </FormControl>
              </div>
              <div className="form-group">
                <FormControl>
                  <FormControlLabel
                    label={formatMessage(messages.canDeleteJobSeries)}
                    disabled={submitting}
                    className="form-label"
                    control={
                      <Field
                        name="canDeleteJobSeries"
                        component={Checkbox}
                        type="checkbox"
                        color="primary"
                      />
                    }
                  />
                </FormControl>
              </div>
            </>
          )}
          {roleLevel >= RoleHelper.ROLE_SUPERADMIN_LEVEL && (
            <>
              <div className="form-group">
                <FormControl>
                  <FormControlLabel
                    label={formatMessage(messages.canSeeDeletedJobs)}
                    disabled={submitting}
                    className="form-label"
                    control={
                      <Field
                        parse={value => {
                          mutators.handleCanSeeDeletedJobsChange(value)

                          return value
                        }}
                        name="canSeeDeletedJobs"
                        component={Checkbox}
                        type="checkbox"
                        color="primary"
                      />
                    }
                  />
                </FormControl>
              </div>
              <div className="form-group">
                <FormControl>
                  <FormControlLabel
                    label={formatMessage(messages.canRecoverDeletedJobs)}
                    disabled={submitting || !values.canSeeDeletedJobs}
                    className="form-label"
                    control={
                      <Field
                        name="canRecoverDeletedJobs"
                        component={Checkbox}
                        type="checkbox"
                        color="primary"
                      />
                    }
                  />
                </FormControl>
              </div>
            </>
          )}
          <div className="form-group">
            <FormControl>
              <FormControlLabel
                label={formatMessage(messages.canReopenJobs)}
                disabled={submitting}
                className="form-label"
                control={
                  <Field
                    name="canReopenJobs"
                    component={Checkbox}
                    type="checkbox"
                    color="primary"
                  />
                }
              />
            </FormControl>
          </div>
          {user && roleLevel >= RoleHelper.ROLE_SUPERADMIN_LEVEL && (
            <div className="form-group">
              <FormControl>
                <FormControlLabel
                  label={formatMessage(messages.canManageWorkflows)}
                  disabled={submitting}
                  className="form-label"
                  control={
                    <Field
                      name="canManageWorkflows"
                      component={Checkbox}
                      type="checkbox"
                      color="primary"
                    />
                  }
                />
              </FormControl>
            </div>
          )}
          <div className="form-group">
            <FormControl>
              <FormControlLabel
                label={formatMessage(messages.canSetSensorsLimits)}
                disabled={submitting}
                className="form-label"
                control={
                  <Field
                    name="canSetSensorsLimits"
                    component={Checkbox}
                    type="checkbox"
                    color="primary"
                  />
                }
              />
            </FormControl>
          </div>
          <div className="form-group">
            <Button
              href={null}
              type="submit"
              color="primary"
              size="medium"
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting || pristine}>
              {formatMessage(messages.saveBtn)}
            </Button>
          </div>
        </form>
      )}
    />
  )
}

UserForm.propTypes = {
  applications: PropTypes.array.isRequired,
  siteList: PropTypes.array.isRequired,
  userGroupList: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
  intl: REACT_INTL_PROP_TYPE.isRequired,
  user: PropTypes.object,
  roles: PropTypes.object,
  licensees: PropTypes.array,
  roleLevel: PropTypes.number.isRequired,
  handleLicenseeChange: PropTypes.func.isRequired,
  handleSiteChange: PropTypes.func.isRequired,
}

UserForm.defaultProps = {
  user: {},
  roles: {},
  licensees: [],
}

export default injectIntl(UserForm)
